import React from "react"
import { useContentfulConditions } from "./hooks/useContentfulConditions"
import { getCountryCode } from "../../reducers/companyInfo"
import { useSelector } from "react-redux"
import useCarrotPlan from "components/carrot-plans/hooks/useCarrotPlan"
import useBenefit from "./hooks/useGetBenefit"
import { HttpStatusCodes } from "../../utils/HttpStatusCodes"
import { useAuthentication } from "../context/authentication/AuthenticationProvider"

export enum AttributeConditionType {
  CANT_HAVE = "cantHave",
  MUST_HAVE = "mustHave",
  HAS_ANY = "hasAny"
}
export enum CountryConditionType {
  CANT_HAVE_COUNTRY = "cantHaveCountry",
  HAS_COUNTRY = "hasCountry"
}

export type Condition = {
  conditionType: AttributeConditionType | CountryConditionType
  attributes: Array<string>
}

export type ConditionalGatePropsTest = {
  conditions: Array<Condition>
}

type UseConditionGateArgs = Array<{ element: React.ReactNode; conditions: Array<Condition> }>

function getUserStateValues(
  attributeKeys: Array<string>,
  conditionsMap: Record<string, { value: boolean; toggle: (condition?: boolean) => void }>
): Array<boolean> {
  return attributeKeys.map((key) => {
    if (typeof conditionsMap[key] === "undefined") {
      throw new Error(`No mapping for Contentful attribute with key: ${key}`)
    } else if (typeof conditionsMap[key].value === "boolean") {
      return conditionsMap[key].value
    }

    throw Error(
      `Invalid attribute value when mapping Contentful attributes. key: ${key}, conditionsMap[key]: ${JSON.stringify(
        conditionsMap[key]
      )}`
    )
  })
}

export function useDoesUserSatisfyConditions(): {
  doesUserSatisfyConditions: (conditions: Array<Condition>) => boolean
  isLoading: boolean
} {
  const conditionsMap = useContentfulConditions()
  const companyCountryCode = useSelector(getCountryCode)

  const benefit = useBenefit()
  const carrotPlan = useCarrotPlan()
  const { handleSessionExpiration } = useAuthentication()

  if (benefit.isError) {
    if ([HttpStatusCodes.UNAUTHORIZED, HttpStatusCodes.FORBIDDEN].includes(benefit.error.statusCode)) {
      handleSessionExpiration()
      return {
        doesUserSatisfyConditions: () => false,
        isLoading: false
      }
    }
    throw Error(`Error trying to retrieve benefit: ${JSON.stringify(benefit.error)}`)
  }

  function doesUserSatisfyConditions(conditions): boolean {
    if (!conditions) return true

    for (const { attributes, conditionType } of conditions) {
      switch (conditionType) {
        case AttributeConditionType.CANT_HAVE:
          if (getUserStateValues(attributes, conditionsMap).some(Boolean)) return false
          break
        case AttributeConditionType.MUST_HAVE:
          if (!getUserStateValues(attributes, conditionsMap).every(Boolean)) return false
          break
        case AttributeConditionType.HAS_ANY:
          if (!getUserStateValues(attributes, conditionsMap).some(Boolean)) return false
          break
        case CountryConditionType.CANT_HAVE_COUNTRY:
          if (attributes.includes(companyCountryCode)) return false
          break
        case CountryConditionType.HAS_COUNTRY:
          if (!attributes.includes(companyCountryCode)) return false
          break
      }
    }

    return true
  }
  return {
    doesUserSatisfyConditions,
    isLoading: benefit.isLoading || carrotPlan.isLoading
  }
}

export function useConditionGate(componentsAndConditions: UseConditionGateArgs): Array<React.ReactNode> {
  const { doesUserSatisfyConditions } = useDoesUserSatisfyConditions()
  return componentsAndConditions
    .map(({ element, conditions }) => doesUserSatisfyConditions(conditions) && element)
    .filter(Boolean)
}

export function ConditionalGate({
  children,
  conditions
}: React.PropsWithChildren<ConditionalGatePropsTest>): JSX.Element {
  const [child] = useConditionGate([{ element: children, conditions }])
  return <>{child}</> || null
}
